import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Wifi, WifiOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { DEMO } from '../lib/demo';

// Indicateur de connexion GLOBAL. Répond à la question des chatteuses : « c'est le
// site qui bug, ou c'est ma connexion ? ». On distingue clairement 3 cas :
//   • offline  : l'appareil n'a plus internet (navigator.onLine)         → c'est TOI
//   • unstable : internet OK mais le serveur ne répond pas (2 échecs)    → c'est TA connexion au serveur
//   • server   : le serveur répond mais en erreur 5xx                    → c'est NOUS (vrai bug)
// Ping léger sur /api/health (endpoint public) toutes les 15 s. Auto-reconnexion :
// le message insiste « pas besoin de redémarrer ton PC » pour tuer ce réflexe.

const HEALTH = `${process.env.REACT_APP_BACKEND_URL}/api/health`;

export default function ConnectionStatus() {
  const [state, setState] = useState('ok'); // 'ok' | 'unstable' | 'offline' | 'server'
  const fails = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (DEMO) return undefined;
    stopped.current = false;

    async function ping() {
      if (stopped.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) { setState('offline'); return; }
      try {
        const r = await axios.get(HEALTH, { timeout: 8000 });
        if (r.status >= 500) throw Object.assign(new Error('server'), { response: { status: r.status } });
        fails.current = 0; setState('ok');
      } catch (e) {
        const status = e?.response?.status;
        fails.current += 1;
        if (status >= 500) setState('server');                 // serveur joignable mais en erreur = vrai bug
        else if (fails.current >= 2) setState(navigator.onLine === false ? 'offline' : 'unstable');
      }
    }
    const onOffline = () => setState('offline');
    const onOnline = () => { fails.current = 0; ping(); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    ping();
    const t = setInterval(ping, 15000);
    return () => {
      stopped.current = true; clearInterval(t);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  if (DEMO) return null;

  if (state === 'ok') {
    return (
      <div className="fixed bottom-3 left-3 z-[60] flex items-center gap-1.5 text-[11px] text-emerald-400/80 bg-black/40 border border-emerald-500/20 rounded-full px-2.5 py-1" style={{ backdropFilter: 'blur(8px)' }}>
        <Wifi size={12} /> Connecté
      </div>
    );
  }

  const cfg = {
    offline: { Icon: WifiOff, color: '#f59e0b', title: 'Hors ligne', msg: "Ton appareil n'a plus internet. Vérifie ton wifi ou ta 4G — l'appli se reconnecte toute seule, pas besoin de redémarrer ton PC." },
    unstable: { Icon: RefreshCw, color: '#f59e0b', spin: true, title: 'Connexion instable', msg: "Ta connexion au serveur est faible (ça vient de ton internet, pas du site). Reconnexion automatique en cours — inutile de redémarrer l'ordinateur." },
    server: { Icon: AlertTriangle, color: '#ef4444', title: 'Serveur indisponible', msg: "C'est un souci de notre côté (pas ta connexion). On est prévenus — réessaie dans un instant." },
  }[state];
  const { Icon } = cfg;

  return (
    <div className="fixed top-0 inset-x-0 z-[70] flex justify-center pointer-events-none p-2">
      <div className="pointer-events-auto flex items-start gap-2 max-w-[600px] rounded-xl px-4 py-2.5 text-[13px] text-white shadow-lg border" style={{ background: cfg.color + '1f', borderColor: cfg.color + '66', backdropFilter: 'blur(10px)' }}>
        <Icon size={16} style={{ color: cfg.color, marginTop: 1 }} className={cfg.spin ? 'animate-spin' : ''} />
        <div><b>{cfg.title}</b> — {cfg.msg}</div>
      </div>
    </div>
  );
}
